import { HttpStatusCode } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { getInsightsPbiChartType, getPbiChartTypeName } from 'src/app/shared/chart/chart';
import { ImportVisualDuplicateHandling, SpendDuplicateImportErrorDto, VisualService } from 'src/app/services/api.services';
import { SnackService } from 'src/app/services/snack.service';
import { catchAppError } from 'src/app/shared/utils';

@Component({
  selector: 'app-visual-import-component',
  templateUrl: './visual-import.component.html',
  styleUrls: ['./visual-import.component.scss'],
  standalone: false
})
export class VisualImportComponent implements OnInit, OnChanges {
  static readonly dialogConfig = <MatDialogConfig>{
    panelClass: 'visual-import-panel',
    hasBackdrop: true,
    disableClose: false,
    width: '600px',
    height: '321px',
  };

  busy = false;
  loading = false;
  
  step: VisualImportStep = VisualImportStep.File;

  file: File;
  fileSelected = false;
  fileName = '';

  visualName = '';
  visualType = '';
  schemaName = '';

  duplicateHandling = ImportVisualDuplicateHandling.Throw;

  @ViewChild('fileInput') _fileInput: ElementRef<HTMLInputElement>;

  fileContentTypes = ['.xanalyzer'];

  showSteps = true;
  showFooter = true;

  copySuffix = 'Copy';

  constructor(
    private snackService: SnackService,
    private dialogRef: MatDialogRef<VisualImportComponent>,
    private visualService: VisualService) { }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
  }

  cancel() {
    this.dialogRef.close();
  }


  back() {
    if (this.step == VisualImportStep.Confirm) {
      this.step = VisualImportStep.File;
    }
  }

  next() {
    if (this.step == VisualImportStep.File) {
      this.getVisualDetails();
    }
    if (this.step == VisualImportStep.UnsupportedType) {
      this.step = VisualImportStep.Confirm;
      this.showFooter = true;
    }
    else if (this.step == VisualImportStep.Confirm) {
      this.importFile();
    }
  }

  replaceExisting() {
    if (this.step == VisualImportStep.DuplicateError) {
      this.step = VisualImportStep.ConfirmReplace;
    }
    else if (this.step == VisualImportStep.ConfirmReplace) {
      this.duplicateHandling = ImportVisualDuplicateHandling.Override;

      this.importFile();
    }
  }

  createCopy() {
    this.duplicateHandling = ImportVisualDuplicateHandling.Copy;

    this.importFile();
  }


  getVisualDetails() {
    this.loading = true;
    this.busy = true;

    this.visualService.getImportDetails({ data: this.file, fileName: this.file.name }).pipe(
      tap(visualDetails => {
        this.visualName = visualDetails.visualName;
        this.visualType = getInsightsPbiChartType(visualDetails.visualType).title;
        this.schemaName = visualDetails.schemaName;

        if (visualDetails.typeSupported) {
          this.step = VisualImportStep.Confirm;
        }
        else {
          this.step = VisualImportStep.UnsupportedType;
          this.showFooter = false;
        }
      }),
      catchAppError(err => {
        this.snackService.openError('Selected file not recognized as a valid .xanalyzer file from legacy Pivot Studio');
    
        return of(null);
      }, { showErrorSnack: false })
    ).subscribe(() => {
      this.busy = false;
      this.loading = false;
    });
  }

  importFile() {
    this.loading = true;
    this.busy = true;

    this.showSteps = false;
    this.showFooter = false;

    this.visualService.importVisual({ data: this.file, fileName: this.file.name }, this.duplicateHandling).pipe(
      tap(visual => {
        this.dialogRef.close({
          visualId: visual.visualId
        });
      }),
      catchAppError(err => {
        if (err.status == HttpStatusCode.Conflict) {
          this.step = VisualImportStep.DuplicateError;

          return err.getErrorContentAsJson().pipe(
            tap(json => {
              this.copySuffix = (<SpendDuplicateImportErrorDto>json)?.newNameSuffix ?? 'Copy';

              if (this.copySuffix.startsWith('- '))
                this.copySuffix = this.copySuffix.substring(2);
            })
          );
        }
        else {
          this.step = VisualImportStep.Confirm;
          this.showSteps = true;
          this.showFooter = true;

          err.showErrorSnack();
        }

        return of(null);
      }, { showErrorSnack: false })
    ).subscribe(() => {
      this.busy = false;
      this.loading = false;
    });
  }


  clearFile() {
    this._fileInput.nativeElement.value = '';

    this.file = null;
    this.fileSelected = false;
    this.fileName = '';
  }

  onFileSelected() {
    if (!this._fileInput.nativeElement.files.length) {
      this.clearFile();
      return;
    }

    this.file = this._fileInput.nativeElement.files.item(0);
    this.fileSelected = true;
    this.fileName = this.file.name;
  }
};

export interface VisualImportResult {
  visualId?: number;
};

enum VisualImportStep {
  File = 'file',
  Confirm = 'confirm',
  UnsupportedType = 'unsupported-type',
  DuplicateError = 'duplicate-error',
  ConfirmReplace = 'confirm-replace'
}
